import { useState, useEffect, useMemo } from "react";
import FormFieldRow from "../FormTags/FormFieldRow/FormFieldRow";
import { InputField } from "../HtmlTags/InputField/InputField";
import { Container } from "../HtmlTags/Container/Container";
import { DatePickerSelect } from "./DatePickerSelect";
import {
  START_YEAR,
  months,
  getDaysInMonth,
  createDateFromValues,
  validateAndAdjustDay,
} from "./utils/datePicker";
import { DatePickerProps, DateValue } from "./types/datePickerTypes";
import "./DatePicker.css";

const DatePicker = ({
  formik,
  label,
  labelFor,
  wrapperClass,
  required,
  error,
}: DatePickerProps) => {
  const [dateValue, setDateValue] = useState<DateValue>({
    day: "",
    month: "",
    year: "",
  });
  const [openDropdownCount, setOpenDropdownCount] = useState(0);

  const currentYear = new Date().getFullYear();

  const years = useMemo(
    () =>
      Array.from(
        { length: currentYear - START_YEAR + 1 },
        (_, i) => currentYear - i
      ),
    [currentYear]
  );

  const daysInMonth = useMemo(() => {
    const monthNum = parseInt(dateValue.month, 10);
    const yearNum = parseInt(dateValue.year, 10);
    return getDaysInMonth(monthNum, yearNum);
  }, [dateValue.month, dateValue.year]);

  const dayOptions = useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, i) => ({
        value: (i + 1).toString(),
        label: (i + 1).toString(),
      })),
    [daysInMonth]
  );

  const monthOptions = useMemo(
    () =>
      months.map((month) => ({
        value: month.value.toString(),
        label: month.name,
      })),
    []
  );

  const yearOptions = useMemo(
    () =>
      years.map((year) => ({ value: year.toString(), label: year.toString() })),
    [years]
  );

  const handleDateChange = (field: keyof DateValue, value: string) => {
    setDateValue((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "month" || field === "year") {
        updated.day = validateAndAdjustDay(
          updated.day,
          updated.month,
          updated.year
        );
      }
      return updated;
    });
  };

  useEffect(() => {
    if (dateValue.day && dateValue.month && dateValue.year) {
      const date = createDateFromValues(
        dateValue.day,
        dateValue.month,
        dateValue.year
      );
      formik.setTouched({
        ...formik.touched,
        [labelFor]: true,
      });
      formik.setFieldValue(labelFor, date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile || openDropdownCount <= 0) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [openDropdownCount]);

  const handleSelectOpenChange = (open: boolean) => {
    setOpenDropdownCount((prev) => {
      if (open) {
        return prev + 1;
      }
      return Math.max(0, prev - 1);
    });
  };

  return (
    <FormFieldRow
      required={required}
      label={label}
      labelFor={labelFor}
      wrapperClass={wrapperClass}
    >
      <Container
        className={`date-picker-field-wrap ${error ? "error" : ""}`}
        data-testid="date-picker-field"
      >
        <DatePickerSelect
          value={dateValue.day}
          onChange={(value) => handleDateChange("day", value)}
          onOpenChange={handleSelectOpenChange}
          placeholder="Day"
          options={dayOptions}
          className="day-wrap"
        />
        <DatePickerSelect
          value={dateValue.month}
          onChange={(value) => handleDateChange("month", value)}
          onOpenChange={handleSelectOpenChange}
          placeholder="Month"
          options={monthOptions}
          className="month-wrap"
        />
        <DatePickerSelect
          value={dateValue.year}
          onChange={(value) => handleDateChange("year", value)}
          onOpenChange={handleSelectOpenChange}
          placeholder="Year"
          options={yearOptions}
          className="year-wrap"
        />
      </Container>
      <InputField
        value={formik.values.date ? formik.values.date : ""}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        name={labelFor}
        id={labelFor}
        type="hidden"
        required={required}
        error={error}
        touched={true}
      />
    </FormFieldRow>
  );
};

export default DatePicker;

/**
 * A comprehensive date picker component that allows users to select a date using
 * separate dropdowns for day, month, and year. Integrates seamlessly with Formik
 * for form state management and validation. Automatically adjusts the day dropdown
 * based on the selected month and year (including leap years).
 *
 * @component
 * @example
 * // Basic usage with Formik
 * <Formik initialValues={{ date: null }} onSubmit={handleSubmit}>
 *   {formik => (
 *     <form>
 *       <DatePicker
 *         formik={formik}
 *         label="Date of Birth"
 *         labelFor="dateOfBirth"
 *         required
 *       />
 *     </form>
 *   )}
 * </Formik>
 *
 * @example
 * // With custom wrapper class
 * <DatePicker
 *   formik={formik}
 *   label="Event Date"
 *   labelFor="eventDate"
 *   wrapperClass="custom-date-wrapper"
 * />
 *
 * @param {DatePickerProps} props - The props for the DatePicker component.
 * @param {FormikProps<any>} props.formik - The Formik form object for managing form state.
 * @param {string} props.label - The label text displayed above the date picker.
 * @param {string} props.labelFor - The id/for attribute linking the label to the input field.
 * @param {string} [props.wrapperClass] - Optional CSS class for custom styling of the wrapper.
 * @param {boolean} [props.required] - Whether the date field is required.
 * @param {string} [props.error] - Optional error message for validation feedback.
 *
 * @returns {JSX.Element} A form field with three select dropdowns for day, month, and year.
 */
